import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { NotificationBell } from "./NotificationBell";
import { STUDIO } from "../data/constants";

/**
 * Top-of-page navigation.
 *
 * Two layouts wrapped in one component:
 *
 *   >= 1100px wide -> desktop. Glassmorphic translucent bar that gets more
 *                  opaque as the user scrolls past the hero. Refined link
 *                  typography (lower weight, tighter tracking), with the
 *                  primary auth CTA as a gradient pill on the right.
 *   <  1100px     -> compact bar with logo + breadcrumb + hamburger. Tapping
 *                  the hamburger slides a drawer panel down with every
 *                  link and the auth actions.
 *
 * The translucent treatment relies on `backdrop-filter: blur(...)`, which
 * works on every modern browser. We also paint a solid background underneath
 * so the bar still reads on browsers that don't support the filter.
 */
export function Nav({ theme, onToggleTheme }) {
  const { profile, session, signOut } = useAuth();
  const loc = useLocation();
  const role = profile?.role || "customer";
  const isDark = theme === "dark";
  const [brandName, setBrandName] = useState(STUDIO);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 1100px)").matches : false,
  );
  const [isNarrowMobile, setIsNarrowMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 430px)").matches : false,
  );
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Live media-query listener so a tablet rotation flips the layout
  // without a reload.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mobileMql = window.matchMedia("(max-width: 1100px)");
    const narrowMql = window.matchMedia("(max-width: 430px)");
    const sync = () => {
      setIsMobile(mobileMql.matches);
      setIsNarrowMobile(narrowMql.matches);
    };
    const listen = (mql) => {
      if (mql.addEventListener) {
        mql.addEventListener("change", sync);
        return () => mql.removeEventListener("change", sync);
      }
      mql.addListener?.(sync);
      return () => mql.removeListener?.(sync);
    };
    sync();
    const removeMobile = listen(mobileMql);
    const removeNarrow = listen(narrowMql);
    return () => {
      removeMobile();
      removeNarrow();
    };
  }, []);

  // Scroll listener — once we're past 24px of scroll, the bar swaps from
  // ultra-transparent to a denser glass so it reads against busy content.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close drawer on route change so it doesn't linger after navigation.
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  useEffect(() => {
    let alive = true;
    fetch("/api/settings/branding")
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (alive && data?.businessName) setBrandName(data.businessName);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Lock body scroll while the drawer is open (kills iOS Safari rubber-band).
  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open, isMobile]);

  const links = [
    { to: "/",        label: "Home",      show: true },
    { to: "/book",    label: "Book",      show: !!session },
    { to: "/gallery", label: "Gallery",   show: true },
    { to: "/history", label: "History",   show: !!session },
    { to: "/chat",    label: "Chat",      show: !!session },
    { to: "/invoices", label: "Invoices", show: !!session },
    { to: "/walkins", label: "Walk-ins",  show: !!session },
    { to: "/tech",    label: "Studio",    show: ["technician", "admin_tech"].includes(role) },
    { to: "/admin",   label: "Admin",     show: role === "admin_tech" },
    { to: "/profile", label: "Profile",   show: !!session },
  ].filter((l) => l.show);

  // Breadcrumb label shown next to the logo on mobile so the user always
  // knows where they are without opening the drawer.
  const here = links.find((l) => l.to === loc.pathname)?.label;

  // Glass background: mostly transparent at the top, denser once scrolled.
  // Two values per theme so the bar feels right whether it's sitting on the
  // ivory landing or a dark in-app surface.
  const glassBg = isDark
    ? scrolled ? "rgba(13, 8, 16, 0.72)" : "rgba(13, 8, 16, 0.32)"
    : scrolled ? "rgba(253, 248, 243, 0.78)" : "rgba(253, 248, 243, 0.42)";
  const glassBorder = isDark
    ? scrolled ? "1px solid rgba(232, 180, 184, 0.10)" : "1px solid transparent"
    : scrolled ? "1px solid rgba(13, 8, 16, 0.06)"     : "1px solid transparent";
  const fg = isDark ? "rgba(255, 252, 250, 0.92)" : "rgba(13, 8, 16, 0.86)";
  const fgDim = isDark ? "rgba(255, 252, 250, 0.58)" : "rgba(13, 8, 16, 0.56)";

  return (
    <header
      style={{
        position: "sticky", top: 0, zIndex: 30,
        background: glassBg,
        borderBottom: glassBorder,
        backdropFilter: "blur(20px) saturate(160%)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        transition: "background .35s var(--ease-out), border-color .35s var(--ease-out)",
      }}
    >
      <div
        style={{
          maxWidth: 1480, margin: "0 auto",
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 22px",
          height: 60,
        }}
      >
        {/* Brand mark */}
        <Link
          to="/"
          aria-label={`${brandName} — Home`}
          style={{
            color: fg, textDecoration: "none",
            display: "inline-flex", alignItems: "center", gap: 10,
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 38, height: 38, borderRadius: 14,
              background: isDark ? "rgba(255,255,255,0.08)" : "rgba(13,8,16,0.06)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(13,8,16,0.08)"}`,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              overflow: "hidden",
              boxShadow: isDark ? "0 8px 18px rgba(0,0,0,0.18)" : "0 8px 18px rgba(13,8,16,0.08)",
            }}
          >
            <img
              src="/app-icons/oge-app-icon-photoreal.png"
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </span>
          <span style={{
            fontFamily: "var(--font-display)",
            fontSize: isMobile ? 15.5 : 17,
            fontWeight: 700,
            letterSpacing: 0,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: isMobile
              ? (isNarrowMobile ? "calc(100vw - 104px)" : "min(48vw, 260px)")
              : "min(30vw, 320px)",
          }}>
            {brandName}
          </span>
        </Link>

        {/* Mobile breadcrumb (lives next to the logo when on a sub-route) */}
        {isMobile && !isNarrowMobile && here && here !== "Home" && (
          <span
            aria-hidden="true"
            style={{
              color: fgDim, fontSize: 13, fontWeight: 500,
              display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0,
            }}
          >
            <span style={{ opacity: 0.55 }}>›</span>
            <span style={{
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{here}</span>
          </span>
        )}

        {/* Desktop link strip — centred, refined typography */}
        {!isMobile && (
          <nav style={{
            display: "flex", gap: 0, marginLeft: 20,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            flexWrap: "nowrap",
          }}>
            {links.filter((l) => l.label !== "Home").map((l) => {
              const active = loc.pathname === l.to;
              return (
                <DesktopLink key={l.to} to={l.to} active={active} fg={fg} fgDim={fgDim}>
                  {l.label}
                </DesktopLink>
              );
            })}
          </nav>
        )}

        {/* Right-side cluster: bell, auth actions */}
        <div style={{
          marginLeft: "auto", display: "flex", gap: 8, alignItems: "center",
          flexShrink: 0,
        }}>
          {session && <NotificationBell />}

          {!isMobile && session && (
            <>
	              <span style={{
	                fontSize: 12.5, color: fgDim, marginRight: 4,
	                display: "inline-flex", alignItems: "center", gap: 8,
	                maxWidth: 320, minWidth: 0, overflow: "hidden",
	              }}>
	                <span style={{
	                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
	                  maxWidth: 210,
	                }} title={profile?.full_name || session.user.email.split("@")[0]}>
	                  {profile?.full_name || session.user.email.split("@")[0]}
	                </span>
                {role !== "customer" && <RoleBadge role={role} />}
              </span>
              <GhostButton fg={fg} onClick={() => signOut()}>Sign out</GhostButton>
            </>
          )}

          {!isMobile && !session && (
            <>
              <Link to="/login" style={{
                color: fg, textDecoration: "none",
                fontSize: 13.5, fontWeight: 500,
                padding: "8px 14px",
              }}>Sign in</Link>
              <PrimaryPill to="/signup">Get started</PrimaryPill>
            </>
          )}

          {isMobile && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="primary-mobile-nav"
              style={{
                width: 40, height: 40, borderRadius: 12,
                background: isDark ? "rgba(255,255,255,0.08)" : "rgba(13,8,16,0.06)",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.18)" : "rgba(13,8,16,0.12)"}`,
                color: fg, cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Hamburger open={open} />
            </button>
          )}
        </div>
      </div>

      {/* Mobile drawer — slides down with all links + auth */}
      {isMobile && (
        <div
          id="primary-mobile-nav"
          role="dialog"
          aria-label="Main menu"
          aria-hidden={!open}
          style={{
            maxHeight: open ? "calc(100svh - 60px)" : 0,
            overflow: "hidden",
            transition: "max-height .4s var(--ease-out)",
            background: isDark
              ? "linear-gradient(180deg, rgba(13,8,16,0.92), rgba(13,8,16,0.96))"
              : "linear-gradient(180deg, rgba(253,248,243,0.96), rgba(245,230,232,0.92))",
            backdropFilter: "blur(24px) saturate(160%)",
            WebkitBackdropFilter: "blur(24px) saturate(160%)",
            borderTop: open
              ? `1px solid ${isDark ? "rgba(232, 180, 184, 0.14)" : "rgba(13, 8, 16, 0.06)"}`
              : "1px solid transparent",
          }}
        >
          <div style={{ padding: "12px 18px 22px", display: "grid", gap: 4 }}>
            {links.map((l) => {
              const active = loc.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  style={{
                    color: active ? fg : fgDim,
                    background: active
                      ? (isDark ? "rgba(255,255,255,0.06)" : "rgba(13,8,16,0.04)")
                      : "transparent",
                    padding: "14px 14px",
                    borderRadius: 14,
                    fontSize: 16, fontWeight: 600,
                    textDecoration: "none",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    transition: "background .2s",
                  }}
                >
                  <span>{l.label}</span>
                  {active && <span aria-hidden="true" style={{ color: "var(--gold)" }}>•</span>}
                </Link>
              );
            })}

            <div style={{
              marginTop: 12, padding: "14px 14px",
              borderTop: `1px solid ${isDark ? "rgba(232, 180, 184, 0.14)" : "rgba(13, 8, 16, 0.08)"}`,
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            }}>
              {session ? (
                <>
                  <span style={{
                    fontSize: 13.5, color: fgDim, display: "inline-flex",
                    alignItems: "center", gap: 8, minWidth: 0,
                  }}>
                    <span style={{
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {profile?.full_name || session.user.email.split("@")[0]}
                    </span>
                    {role !== "customer" && <RoleBadge role={role} />}
                  </span>
                  <GhostButton fg={fg} onClick={() => signOut()}>Sign out</GhostButton>
                </>
              ) : (
                <>
                  <Link to="/login" style={{
                    color: fg, textDecoration: "none",
                    fontSize: 14.5, fontWeight: 600, padding: "10px 14px",
                  }}>Sign in</Link>
                  <PrimaryPill to="/signup">Get started</PrimaryPill>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

// ── micro-components ────────────────────────────────────────────────────────

function DesktopLink({ to, active, fg, fgDim, children }) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
	        color: active ? fg : (hover ? fg : fgDim),
	        textDecoration: "none",
	        fontSize: 13.5, fontWeight: 500,
	        letterSpacing: 0,
	        padding: "8px 10px",
	        borderRadius: 999,
	        position: "relative",
	        transition: "color .2s",
	        whiteSpace: "nowrap",
	        flexShrink: 0,
      }}
    >
      {children}
      <span
        aria-hidden="true"
        style={{
          position: "absolute", left: 14, right: 14, bottom: 4,
          height: 2, borderRadius: 999,
          background: active
            ? "linear-gradient(90deg, var(--mauve), var(--mauve-deep))"
            : "transparent",
          transform: active ? "scaleX(1)" : "scaleX(0)",
          transformOrigin: "left",
          transition: "transform .25s var(--ease-spring)",
        }}
      />
    </Link>
  );
}

function PrimaryPill({ to, children }) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
        color: "#fff",
        textDecoration: "none",
        fontSize: 13.5, fontWeight: 700,
        padding: "9px 18px",
        borderRadius: 999,
        boxShadow: hover
          ? "0 10px 24px rgba(196, 135, 154, 0.45)"
          : "0 4px 14px rgba(196, 135, 154, 0.28)",
        transform: hover ? "translateY(-1px)" : "translateY(0)",
        transition: "all .25s var(--ease-spring)",
      }}
    >
      {children}
    </Link>
  );
}

function GhostButton({ fg, onClick, children }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover
          ? "rgba(196, 135, 154, 0.10)"
          : "transparent",
        color: fg,
        border: "1px solid rgba(196, 135, 154, 0.28)",
	        borderRadius: 999,
	        padding: "8px 14px",
	        fontSize: 12.5, fontWeight: 600,
	        cursor: "pointer",
	        transition: "background .2s",
	        whiteSpace: "nowrap",
	        minWidth: 82,
	        lineHeight: 1.2,
      }}
    >
      {children}
    </button>
  );
}

function RoleBadge({ role }) {
  return (
    <span style={{
	      fontSize: 9.5, padding: "2px 8px", borderRadius: 999,
      background: role === "admin_tech"
        ? "rgba(201, 168, 76, 0.22)"
        : "rgba(196, 135, 154, 0.22)",
      color: role === "admin_tech" ? "var(--gold-light)" : "var(--rose)",
	      fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase",
	      whiteSpace: "nowrap",
	      flexShrink: 0,
	    }}>
      {role === "admin_tech" ? "Admin" : "Tech"}
    </span>
  );
}

function Hamburger({ open }) {
  // Three bars animating into an X — pure SVG, no extra deps.
  const common = {
    fill: "currentColor",
    transition: "transform .25s var(--ease-out), opacity .2s",
  };
  return (
    <svg width="20" height="14" viewBox="0 0 20 14" aria-hidden="true">
      <rect x="0" y="0"  width="20" height="2" rx="1"
        style={{ ...common, transformOrigin: "10px 1px",
          transform: open ? "translateY(6px) rotate(45deg)" : "none" }} />
      <rect x="0" y="6"  width="20" height="2" rx="1"
        style={{ ...common, opacity: open ? 0 : 1 }} />
      <rect x="0" y="12" width="20" height="2" rx="1"
        style={{ ...common, transformOrigin: "10px 13px",
          transform: open ? "translateY(-6px) rotate(-45deg)" : "none" }} />
    </svg>
  );
}
